import React, { useState } from 'react';
import { 
  Building, Heart, Target, Briefcase, Home, Users,
  ArrowRight, CheckCircle, Star, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';

const SolutionsGrid = ({ solutions, openBooking }) => {
  const [hoveredCard, setHoveredCard] = useState(null);

  const iconMap = {
    Building, Heart, Target, Briefcase, Home, Users
  };

  const defaultSolutions = [
    {
      id: 1,
      title: 'SIL Providers',
      description: 'Supported Independent Living management with house-based rostering, participant care tracking, and compliance monitoring.',
      icon: 'Home',
      color: 'care-teal',
      features: ['24/7 shift management', 'House compliance tracking', 'Incident reporting', 'Medication management'],
      image_url: 'https://images.pexels.com/photos/7551667/pexels-photo-7551667.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 2,
      title: 'Support Coordination',
      description: 'Streamline your support coordination with participant journey tracking, goal management, and automated NDIS reporting.',
      icon: 'Target',
      color: 'tech-blue',
      features: ['Plan tracking', 'Goal progress monitoring', 'Provider management', 'Report generation'],
      image_url: 'https://images.pexels.com/photos/7551622/pexels-photo-7551622.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 3,
      title: 'Allied Health',
      description: 'Clinical practice management designed for NDIS allied health providers with appointment scheduling and clinical notes.',
      icon: 'Heart',
      color: 'heritage-orange',
      features: ['Clinical documentation', 'Appointment management', 'Outcome tracking', 'Service agreements'],
      image_url: 'https://images.pexels.com/photos/7551619/pexels-photo-7551619.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 4,
      title: 'Plan Management',
      description: 'Complete plan management solution with budget tracking, invoice processing, and financial reporting.',
      icon: 'Briefcase',
      color: 'purple-500',
      features: ['Budget tracking', 'Invoice processing', 'Provider payments', 'Financial reports'],
      image_url: 'https://images.pexels.com/photos/8127501/pexels-photo-8127501.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const displaySolutions = solutions?.length > 0 ? solutions : defaultSolutions;

  return (
    <section className="py-24 bg-gradient-to-b from-page-bg via-soft-sky/30 to-page-bg relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-tech-blue/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-0 w-[600px] h-[600px] bg-care-teal/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-tech-blue/10 to-care-teal/10 rounded-full border border-tech-blue/20 mb-6">
            <Sparkles className="text-tech-blue mr-2" size={18} />
            <span className="text-sm font-semibold bg-gradient-to-r from-tech-blue to-care-teal bg-clip-text text-transparent">
              Tailored for Your Organisation
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-navy mb-4">
            Solutions for Every{' '}
            <span className="relative">
              <span className="bg-gradient-to-r from-tech-blue to-care-teal bg-clip-text text-transparent">
                Australian NDIS Provider
              </span>
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                <path d="M2 8 Q75 2 150 8 T298 8" stroke="url(#gradient)" strokeWidth="3" strokeLinecap="round" fill="none"/>
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#1E6FD9" />
                    <stop offset="100%" stopColor="#00A89D" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto mt-6">
            Each platform is custom-built for your specific NDIS operational requirements
          </p>
        </div>

        {/* Solutions Grid - Bento Style */}
        <div className="grid md:grid-cols-2 gap-6">
          {displaySolutions.slice(0, 4).map((solution, index) => {
            const Icon = iconMap[solution.icon] || Building;
            const isHovered = hoveredCard === solution.id;
            const isLarge = index === 0 || index === 3;
            
            return (
              <div
                key={solution.id}
                onMouseEnter={() => setHoveredCard(solution.id)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`group relative rounded-3xl overflow-hidden transition-all duration-500 ${
                  isLarge ? 'md:row-span-1' : ''
                } ${isHovered ? 'scale-[1.02] shadow-2xl shadow-tech-blue/20' : 'shadow-xl'}`}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img 
                    src={solution.image_url} 
                    alt={solution.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br from-primary-navy/95 via-primary-navy/80 to-transparent transition-opacity duration-500 ${
                    isHovered ? 'opacity-95' : 'opacity-90'
                  }`}></div>
                </div>

                {/* Content */}
                <div className="relative p-8 h-full min-h-[320px] flex flex-col">
                  {/* Badge */}
                  <div className="absolute top-6 right-6">
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 transition-all duration-300 ${
                      isHovered ? 'bg-care-teal/20 border-care-teal/30' : ''
                    }`}>
                      <span className="text-lg">🇦🇺</span>
                      <span className="text-xs text-white/80">Australian Focused</span>
                    </div>
                  </div>

                  {/* Icon & Title */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                      isHovered 
                        ? 'bg-care-teal scale-110 rotate-3' 
                        : 'bg-white/10'
                    }`}>
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{solution.title}</h3>
                  </div>

                  {/* Description */}
                  <p className="text-white/70 mb-6 leading-relaxed">
                    {solution.description}
                  </p>

                  {/* Features */}
                  <div className="flex-1">
                    <div className={`grid grid-cols-2 gap-3 transition-all duration-500 ${
                      isHovered ? 'opacity-100 translate-y-0' : 'opacity-70'
                    }`}>
                      {(solution.features || []).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle className={`flex-shrink-0 transition-colors duration-300 ${
                            isHovered ? 'text-care-teal' : 'text-white/50'
                          }`} size={16} />
                          <span className="text-sm text-white/80">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className={`mt-6 transition-all duration-500 ${
                    isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}>
                    <Button 
                      onClick={openBooking}
                      className="bg-care-teal hover:bg-care-teal/90 text-white group/btn"
                    >
                      Book a Demo
                      <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={18} />
                    </Button>
                  </div>

                  {/* Decorative corner */}
                  <div className={`absolute bottom-0 right-0 w-32 h-32 transition-all duration-500 ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`}>
                    <div className="absolute bottom-4 right-4 w-20 h-20 border-2 border-care-teal/30 rounded-full"></div>
                    <div className="absolute bottom-8 right-8 w-12 h-12 border-2 border-tech-blue/30 rounded-full"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-tech-blue/10 to-care-teal/10 rounded-2xl border border-tech-blue/20">
            <Star className="text-heritage-orange" size={24} />
            <span className="text-text-primary font-medium">
              Don't see your provider type? We build custom solutions for{' '}
              <span className="text-tech-blue font-semibold">any NDIS service</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionsGrid;
